import { Input } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { addChangeListener } from 'stores/DynamicProperties/changeListeners';
import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import { transformToNumber } from 'stores/DynamicProperties/parse';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import { nudge } from 'utils/InputUtils';

import { ScalarContainer, type ScalarInputSize } from './ScalarContainer';

// Was styled(AutoComplete) until the autocomplete feature was confirmed dead:
// no options were ever passed, so it rendered an empty dropdown that could not
// open. Input renders the same field without the combobox machinery, and
// reports a textbox rather than a combobox to assistive tech.
const PropertyInput = styled(Input)`
    flex-grow: 1;
    width: 100%;
`;

const AfterLabel = styled.div`
    color: #a2a2a2;
    padding: 0 11px;
    background: #f3f3f3;
    border: 1px solid #d6d6d6;
    height: 32px;
    margin-left: -3px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

function isValidNumber(value: string) {
    // TODO: .endsWith('.') preserves prior behavior, but it's not clear
    //  why an ending dot should be considered invalid
    return value.length > 0 && isFinite(+value) && !value.endsWith('.');
}

function isNumericType(type: DynamicPropertyType) {
    return (
        type === DynamicPropertyType.NUMBER ||
        type === DynamicPropertyType.PIXELS ||
        type === DynamicPropertyType.PERCENTAGE
    );
}

export interface TextInputProps {
    property: IDynamicProperty<any>;
    size?: ScalarInputSize;
    disabled?: boolean;
}

// Not an observer, deliberately. Nothing read during render is observable: a
// leaf DynamicProperty never calls becomeObservable, and the only path that
// would make one observable - DynamicGroup.enableState - has no callers.
// Re-rendering comes entirely from the subscription below.
function TextInput({ disabled, property, size }: TextInputProps) {
    const [focused, setFocused] = useState(false);
    const [text, setText] = useState(String(property.value));
    const [inputError, setInputError] = useState(false);

    // The following two state vars are only used when the TextInput is
    // validating numbers (see isNumericType).
    //
    // `lastValidNumber` follows the most recent entry that passed
    // isValidNumber; `numberAtFocus` is frozen at what the property held
    // when focus arrived, and is used when the field has been emptied
    // rather than mistyped.
    //
    // These are refs rather than state because neither is read during
    // render - nothing displays them, so a write should not schedule one.
    const lastValidNumber = useRef<number>(0);
    const numberAtFocus = useRef<number>(0);

    // The CHANGED handler has to know whether the field is focused *now*, but
    // listing `focused` among that effect's dependencies would tear down and
    // rebuild the property subscription on every focus and blur. A ref reads
    // the current value without re-binding anything.
    //
    // Assigned by the focus handlers rather than mirrored from `focused`
    // during render, and that distinction matters: handleBlur calls
    // property.set in the same event that clears the focus, and React does not
    // re-render until the handler returns. A ref synced at render time would
    // still read `true` inside the resulting CHANGED, so the handler below
    // would decline to refresh the display and the field would sit there empty
    // and red over a property that had just been restored. The ref has to lead
    // the state here, not follow it.
    const focusedRef = useRef(false);

    // useCallback because this is a dependency of both effects below. Without
    // a stable identity they would see a changed dependency on every render,
    // tearing down and rebuilding the property subscription and the keydown
    // listener each time - leaving a window on every render with nothing
    // subscribed. handleFocus and handleBlur need no such treatment: they only
    // reach DOM props, where a fresh identity costs nothing.
    const applyValue = useCallback(
        (rawValue: unknown, syncToProperty: boolean) => {
            // Normalized once here rather than at each call site: the nudge
            // site passes a number, and the property-event site passes
            // property.value, which is `any`. This is the boundary that pins
            // them down - `text` is a string and stays one.
            const value = String(rawValue);
            setText(value);

            if (!isNumericType(property.type)) {
                if (syncToProperty) property.set(value);
                return;
            }

            if (isValidNumber(value)) {
                setInputError(false);
                if (syncToProperty) property.set(Number(value));
                lastValidNumber.current = Number(value);
            } else {
                setInputError(true);
                // Note: used to not be guarded by syncToProperty, but given
                // that syncToProperty is false when handling events
                // dispatched *by* the property, it seems this should be
                // gated as well.
                if (syncToProperty && value === '') {
                    property.set(property.initialValue);
                }
            }
        },
        [property]
    );

    useEffect(() => {
        // Seed from the property being edited. Redundant at mount, where
        // useState already did it, but necessary if `property` changes
        // identity.
        setText(String(property.value));
        setInputError(false);

        // syncToProperty: false. The property is what just changed, so
        // writing it back would be a round trip at best and a re-entrant set
        // at worst - this is a display-side update only.
        //
        // This also covers revert(), which used to dispatch a separate
        // REVERTED that skipped the focus check. Every revert comes from a
        // button, and clicking one blurs this field first, so the check
        // never stood in its way.
        const onPropertyChanged = () => {
            // Don't overwrite what the user is part-way through typing.
            if (focusedRef.current) return;
            applyValue(property.value, false);
            // note: unlike other property change listeners in sibling
            //  components, forceUpdate is not necessary here: render reads
            //  only state, props and property.unit, and applyValue updates
            //  the state when the display should change.
        };

        return addChangeListener(property, onPropertyChanged);
    }, [property, applyValue]);

    useEffect(() => {
        if (!focused) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isNumericType(property.type)) return;

            const direction =
                e.key === 'ArrowUp'
                    ? 1
                    : e.key === 'ArrowDown'
                      ? -1
                      : undefined;
            if (!direction) return;

            // Stepping is deliberately taken from the property rather than the
            // visible text, so a half-typed entry does not become the basis of
            // the arithmetic.
            const current = Number(property.value);
            if (!Number.isFinite(current)) return;

            e.preventDefault();
            applyValue(nudge(current, direction, e), true);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [focused, property, applyValue]);

    const handleFocus = () => {
        focusedRef.current = true;
        setFocused(true);
        if (!inputError) {
            // property.value is `any`, and a numeric property can hold a
            // non-number - the constructor assigns without running transform.
            // Seeding through the same coercion the property itself would
            // apply means these can never disagree with what property.set
            // would have stored, and keeps the declared `number` true.
            // Unreadable input becomes 0, which is the fallback these had
            // before any focus anyway.
            const seed = transformToNumber(property.value, property);
            lastValidNumber.current = seed;
            numberAtFocus.current = seed;
        }
    };

    const handleBlur = () => {
        // Before the property.set below, not after: that set dispatches CHANGED
        // synchronously, and the handler it reaches must already see the field
        // as unfocused or it will decline to refresh the display.
        focusedRef.current = false;
        setFocused(false);
        if (inputError) {
            // Emptied, so restore what was there on arrival; otherwise
            // mistyped, so restore the last entry that parsed.
            property.set(
                text === '' ? numberAtFocus.current : lastValidNumber.current
            );
        }
    };

    return (
        <ScalarContainer
            size={size}
            error={inputError}
            showUnits={!!property.unit}
        >
            <PropertyInput
                disabled={disabled}
                placeholder="Enter value"
                onChange={(e) => applyValue(e.target.value, true)}
                value={text}
                onBlur={handleBlur}
                onFocus={handleFocus}
            />
            {property.unit && <AfterLabel>{property.unit}</AfterLabel>}
        </ScalarContainer>
    );
}

export default TextInput;
